import {
  isNaN, isNil, startCase, toLower, last, isEmpty,
} from 'lodash';
import moment from 'moment-timezone';
import { ReactComponent as IconTrain } from '../../../../assets/svg/train.svg';
import { ReactComponent as IconBus } from '../../../../assets/svg/bus.svg';
import { ReactComponent as IconFerry } from '../../../../assets/svg/ferry.svg';
import { DUE_MINS_THRESHOLD } from '../../../../constants/liveDepartures';
import { isCancelled, routeCodeToName } from '../../../../services/LiveDepartureService';

// Note: we also use the plural form for zero (0) items
const pluralise = (count, word, customPluralWord) => count === 1 ? word : customPluralWord ?? `${word}s`;

const getArrivalStatusDisplay = (isArriving, isDeparted, disablePredictionDisplay) => {
  if (disablePredictionDisplay) {
    return null;
  } else if (isDeparted) {
    return 'Departed';
  } else if (isArriving) {
    return 'Arriving';
  } else {
    return null;
  }
};

const createHeadSignDisplay = (destination) => {
  if (!destination) {
    return null;
  }

  const headSign = `to ${startCase(toLower(destination))}`;
  const components = headSign.split(' ');
  const lastComponent = last(components);
  if (components.length > 1 && lastComponent && lastComponent.length === 1) {
    return `${components.slice(0, components.length - 1).join(' ')}/${lastComponent}`;
  }

  return headSign;
};

// To improve: this function is too long, needs break-up/clean-up
export const getDisplays = (movement, mode, disablePredictionDisplay) => {
  const {
    arrivalStatus,
    arriving,
    departed,
    departurePlatformName: platformName,
    destinationDisplay,
    route_short_name: routeName,
    stop_headsign: stopHeadSign,
    stopsAway,
    waitingTime
  } = movement;

  const platformNameAria = mode === 'TRAIN' && !isEmpty(platformName) ? `, platform: ${platformName}` : '';
  const platformDisplay = platformName && `PLAT ${platformName}`;

  const destination = destinationDisplay || stopHeadSign;
  const destinationAria = `destination: ${destination} ${platformNameAria}`;
  const headSignDisplay = createHeadSignDisplay(destination);

  const routeNameAria = routeCodeToName(routeName, mode);
  const routeNameDisplay = routeName;

  const cancelledDisplay = isCancelled(arrivalStatus) ? 'Cancelled' : null;
  const cancelledAria = cancelledDisplay ? 'is cancelled' : '';

  const arrivalStatusDisplay = getArrivalStatusDisplay(arriving, departed, disablePredictionDisplay);
  const arrivalStatusAria = cancelledAria || !arrivalStatusDisplay ? '' : `is ${arrivalStatusDisplay}`;

  const shouldHideTime = cancelledDisplay || arrivalStatusDisplay;

  const dueMin = waitingTime ? Math.floor(waitingTime / 60 / 1000) : null;
  const doDisplayDueTime = !disablePredictionDisplay && !shouldHideTime && dueMin && dueMin > 0 && dueMin < DUE_MINS_THRESHOLD
  const dueTimeDisplay = doDisplayDueTime ? `${dueMin} ${pluralise(dueMin, 'min')} ` : null;
  const dueTimeAria = doDisplayDueTime ? `, due in ${dueMin} ${pluralise(dueMin, 'minute')}` : '';

  const stopsAwayStr = isNil(stopsAway) || isNaN(stopsAway) || stopsAway < 1 ? '' : `${stopsAway} ${pluralise(stopsAway, 'stop')}`;
  const stopsAwayDisplay = dueTimeDisplay && stopsAwayStr && `(${stopsAwayStr})`;
  const stopsAwayAria = stopsAwayStr ? `${stopsAwayStr} away` : '';

  const scheduledTimeStr = movement.scheduledDepartureTime ? moment(movement.scheduledDepartureTime).format('HH:mm') : '';
  const scheduledTimeDisplay = (disablePredictionDisplay || (!dueTimeDisplay && !arrivalStatusDisplay)) ? `Scheduled ${scheduledTimeStr}` : null;
  const scheduledTimeAria = `scheduled at : ${scheduledTimeStr}`;

  const dueTimeStopsAwayAria = shouldHideTime ? '' : `${dueTimeAria} ${stopsAwayAria}`;

  const ariaLabel = `${routeNameAria}, ${destinationAria}, ${scheduledTimeAria}, ${cancelledAria}, ${arrivalStatusAria}, ${dueTimeStopsAwayAria}`;

  return Object.assign({}, movement, {
    ariaLabel,
    dueTimeDisplay,
    stopsAwayDisplay,
    routeNameDisplay,
    cancelledDisplay,
    arrivalStatusDisplay,
    platformDisplay,
    scheduledTimeDisplay,
    headSignDisplay,
  });
};

export const getIcon = (mode) => {
  let SvgIcon = null;
  let iconClass = '';
  switch (mode) {
    case 'BUS':
      SvgIcon = IconBus;
      break;
    case 'TRAIN':
      SvgIcon = IconTrain;
      iconClass = 'movement-info-icon-train';
      break;
    case 'FERRY':
      SvgIcon = IconFerry;
      iconClass = 'movement-info-icon-ferry';
      break;
    default:
      break;
  }

  return {
    SvgIcon,
    iconClass,
  };
};
